import React, { useState, useEffect } from 'react';
import { CreditCard, RefreshCw, Settings, Activity, Loader2, Link2, Unlink, BookOpen } from 'lucide-react';
import { apiService } from '../services/api';

interface Platform {
  id: string;
  name: string;
  type: string;
  status: string;
  lastSync: string;
  revenue: number;
  transactions: number;
  successRate: number;
  isActive: boolean;
}

interface QBOCompanyStatus {
  realmId: string;
  companyName?: string;
  connected: boolean;
  environment: string;
  tokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
  needsReauth: boolean;
}

interface QBOStatus {
  configured: boolean;
  companies: QBOCompanyStatus[];
  connectedCount: number;
  message?: string;
}

const Platforms: React.FC = () => {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [qboStatus, setQboStatus] = useState<QBOStatus | null>(null);
  const [qboLoading, setQboLoading] = useState(false);

  useEffect(() => {
    loadPlatforms();
    loadQBOStatus();

    // Check for QBO callback params in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('qb_connected') === 'true') {
      loadQBOStatus();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('qb_error')) {
      alert(`QuickBooks connection error: ${params.get('qb_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadPlatforms = async () => {
    try {
      setLoading(true);
      
      // Load platforms from the API instead of hardcoded definitions
      await apiService.loadPlatforms();
      const platformList = await apiService.getPlatforms();
      
      // Get transaction data for each platform
      const platformData = await Promise.all(
        platformList.map(async (platform) => {
          try {
            const response = await apiService.getTransactions(
              1, // page
              1000, // pageSize
              platform.id, // platformFilter
              undefined // statusFilter
            );
            
            const transactions = response.data || [];
            const successfulTransactions = transactions.filter(tx => tx.status === 'success');
            
            // All amounts are stored as dollars, no conversion needed
            const revenue = successfulTransactions.reduce((sum, tx) => sum + tx.amount, 0);
            
            const successRate = transactions.length > 0 ? (successfulTransactions.length / transactions.length) * 100 : 0;
            
            // Get last sync time from most recent transaction
            const lastSync = transactions.length > 0 
              ? new Date(Math.max(...transactions.map(tx => new Date(tx.created_at).getTime())))
              : new Date();
            
            // Determine platform type from account name
            let platformType = 'unknown';
            if (platform.account_name.toLowerCase().includes('stripe')) {
              platformType = 'stripe';
            } else if (platform.account_name.toLowerCase().includes('authorize')) {
              platformType = 'authorizenet';
            } else if (platform.account_name.toLowerCase().includes('shopify')) {
              platformType = 'shopify';
            }
            
            return {
              id: platform.id,
              name: platform.account_name,
              type: platformType,
              status: 'active',
              lastSync: formatTimeAgo(lastSync),
              revenue,
              transactions: transactions.length,
              successRate,
              isActive: true
            };
          } catch (error) {
            console.error(`Error loading data for platform ${platform.account_name}:`, error);
            return {
              id: platform.id,
              name: platform.account_name,
              type: 'unknown',
              status: 'error',
              lastSync: 'Never',
              revenue: 0,
              transactions: 0,
              successRate: 0,
              isActive: false
            };
          }
        })
      );

      setPlatforms(platformData);
    } catch (error) {
      console.error('Error loading platforms:', error);
      // Set empty array to prevent undefined errors
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  const loadQBOStatus = async () => {
    try {
      const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';
      const response = await fetch(`${API_BASE}/quickbooks/status`);
      const result = await response.json();
      if (result.success) {
        setQboStatus(result.data);
      }
    } catch (error) {
      console.error('Error loading QuickBooks status:', error);
    }
  };

  const handleQBOConnect = async () => {
    try {
      setQboLoading(true);
      const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';
      const response = await fetch(`${API_BASE}/quickbooks/authorize`);
      const result = await response.json();
      if (result.success && result.data.authUrl) {
        window.location.href = result.data.authUrl;
      }
    } catch (error) {
      console.error('Error initiating QuickBooks auth:', error);
      alert('Failed to start QuickBooks connection. Check that the backend is running.');
    } finally {
      setQboLoading(false);
    }
  };

  const handleQBODisconnect = async (realmId: string, companyName?: string) => {
    if (!confirm(`Are you sure you want to disconnect ${companyName || realmId} from QuickBooks?`)) return;
    try {
      setQboLoading(true);
      const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';
      await fetch(`${API_BASE}/quickbooks/disconnect/${realmId}`, { method: 'POST' });
      await loadQBOStatus();
    } catch (error) {
      console.error('Error disconnecting QuickBooks:', error);
    } finally {
      setQboLoading(false);
    }
  };

  const handleSyncPlatform = async (platformId: string) => {
    try {
      setSyncing(platformId);
      await apiService.syncPlatform(platformId);
      
      // Reload platform data after successful sync
      await loadPlatforms();
      const platform = platforms.find(p => p.id === platformId);
      alert(`${platform?.name || platformId} sync completed successfully!`);
    } catch (error) {
      console.error(`Error syncing platform ${platformId}:`, error);
      const platform = platforms.find(p => p.id === platformId);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error syncing ${platform?.name || platformId}: ${errorMessage}`);
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncAllPlatforms = async () => {
    try {
      setSyncing('all');
      const platformIds = platforms.map(p => p.id);
      
      if (platformIds.length === 0) {
        alert('No platforms available to sync.');
        return;
      }
      
      // Sync all platforms in parallel
      const syncPromises = platformIds.map(platformId => apiService.syncPlatform(platformId));
      const results = await Promise.allSettled(syncPromises);
      
      // Count successful and failed syncs
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;
      
      // Reload platform data
      await loadPlatforms();
      
      if (failed === 0) {
        alert(`All ${successful} platforms synced successfully!`);
      } else {
        alert(`${successful} platforms synced successfully, ${failed} failed. Check console for details.`);
      }
    } catch (error) {
      console.error('Error syncing all platforms:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error syncing platforms: ${errorMessage}`);
    } finally {
      setSyncing(null);
    }
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  const getPlatformIcon = (type: string) => {
    switch (type) {
      case 'stripe':
        return <CreditCard className="h-6 w-6 text-blue-600" />;
      case 'authorizenet':
        return <CreditCard className="h-6 w-6 text-green-600" />;
      case 'shopify':
        return <CreditCard className="h-6 w-6 text-purple-600" />;
      case 'quickbooks':
        return <BookOpen className="h-6 w-6 text-emerald-600" />;
      default:
        return <CreditCard className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-red-600 bg-red-100';
      case 'error': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 98) return 'text-green-600';
    if (rate >= 95) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading platforms...</span>
      </div>
    );
  }

  if (platforms.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payment Platforms</h1>
            <p className="text-gray-600">Manage and monitor your payment platform integrations</p>
          </div>
        </div>
        
        {/* Empty State */}
        <div className="text-center py-12">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No platforms found</h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no payment platforms configured or available.
          </p>
          <div className="mt-6">
            <button 
              className="btn-primary"
              onClick={loadPlatforms}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Platforms</h1>
          <p className="text-gray-600">Manage and monitor your payment platform integrations</p>
        </div>
        <button 
          className="btn-primary flex items-center"
          onClick={handleSyncAllPlatforms}
          disabled={syncing === 'all'}
        >
          {syncing === 'all' ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {syncing === 'all' ? 'Syncing...' : 'Sync All Platforms'}
        </button>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {platforms.map((platform) => (
          <div key={platform.id} className="card">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                {getPlatformIcon(platform.type)}
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{platform.name}</h3>
                  <p className="text-sm text-gray-500 capitalize">{platform.type}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(platform.status)}`}>
                  {platform.status}
                </span>
                <button className="text-gray-400 hover:text-gray-600">
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Revenue</p>
                <p className="text-lg font-semibold text-gray-900">
                  ${platform.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Transactions</p>
                <p className="text-lg font-semibold text-gray-900">
                  {platform.transactions.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Success Rate</p>
                <p className={`text-lg font-semibold ${getSuccessRateColor(platform.successRate)}`}>
                  {platform.successRate.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Last Sync</p>
                <p className="text-sm text-gray-900">{platform.lastSync}</p>
              </div>
            </div>

            {/* Additional Info Row */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Platform ID:</span>
                <span className="font-mono text-gray-700">{platform.id.slice(0, 8)}...</span>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">
                  {platform.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <button
                onClick={() => handleSyncPlatform(platform.id)}
                disabled={syncing === platform.id}
                className="btn-secondary flex items-center text-sm"
              >
                {syncing === platform.id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {syncing === platform.id ? 'Syncing...' : 'Sync'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* QuickBooks Integration Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">QuickBooks Online</h2>
          {qboStatus?.configured !== false && (
            <button
              onClick={handleQBOConnect}
              disabled={qboLoading}
              className="btn-primary flex items-center text-sm"
            >
              {qboLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Connect a Company
            </button>
          )}
        </div>

        {qboStatus?.configured === false ? (
          <div className="card">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-6 w-6 text-gray-400" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">QuickBooks Not Configured</h3>
                <p className="text-sm text-gray-500">
                  Add INTUIT_CLIENT_ID and INTUIT_CLIENT_SECRET to .env to enable QuickBooks integration
                </p>
              </div>
            </div>
          </div>
        ) : qboStatus?.companies && qboStatus.companies.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {qboStatus.companies.map((company) => (
              <div key={company.realmId} className="card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <BookOpen className="h-6 w-6 text-emerald-600" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {company.companyName || `Realm ${company.realmId}`}
                      </h3>
                      <p className="text-sm text-gray-500 capitalize">{company.environment}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    company.connected
                      ? 'text-green-600 bg-green-100'
                      : company.needsReauth
                      ? 'text-yellow-600 bg-yellow-100'
                      : 'text-red-600 bg-red-100'
                  }`}>
                    {company.connected ? 'Connected' : company.needsReauth ? 'Needs Reauth' : 'Disconnected'}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Realm ID</p>
                    <p className="text-sm font-mono text-gray-700">{company.realmId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Refresh Token Expires</p>
                    <p className="text-sm text-gray-900">
                      {company.refreshTokenExpiresAt
                        ? new Date(company.refreshTokenExpiresAt).toLocaleDateString()
                        : 'Unknown'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end space-x-3">
                  {company.needsReauth && !company.connected && (
                    <button
                      onClick={handleQBOConnect}
                      disabled={qboLoading}
                      className="btn-primary flex items-center text-sm"
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      Reconnect
                    </button>
                  )}
                  <button
                    onClick={() => handleQBODisconnect(company.realmId, company.companyName)}
                    disabled={qboLoading}
                    className="btn-secondary flex items-center text-sm text-red-600 hover:text-red-700"
                  >
                    {qboLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Unlink className="h-4 w-4 mr-2" />
                    )}
                    Disconnect
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-6 w-6 text-gray-400" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">No QuickBooks Companies Connected</h3>
                <p className="text-sm text-gray-500">
                  Click "Connect a Company" to authorize a QuickBooks Online company
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Platforms;